import React, { useState, useEffect } from "react";
import { Modal, Alert } from "react-bootstrap";
import { Trans, useTranslation } from "react-i18next";
import {
  ActionIconContainer,
  AddFormButton,
  CustomLoader,
  FilterButton,
} from "../../../../shared/components/TableElements";
import {
  FormGroupLabel,
  FormItem,
  LinkAnchor,
} from "../../../../shared/components/form/FormElements";
import { formInputStyles } from "../../../../shared/constants/styles";
import {
  handleNegative,
  handleNumberInputOnWheelPreventChange,
} from "../../../../shared/helpers";
import FormField from "../../../../shared/components/form/FormField";
import { useForm } from "react-hook-form";
import Error from "@/shared/components/form/Error";
import { colorSwitchActive } from "@/utils/palette";
import Switch from "@/shared/components/form/Switch/Switch";

const AutoTopUpSettingsModal = ({
  show,
  onHide,
  onSave,
  hasPaymentMethod,
  onAddPaymentMethod,
  autoTopUpData,
  saveAutoTopUpLoading,
}) => {
  const { t } = useTranslation("common");

  const {
    control,
    getValues,
    watch,
    setValue,
    setError,
    clearErrors,
    formState: { errors },
  } = useForm({
    mode: "onBlur",
    defaultValues: {
      enabled: autoTopUpData?.enabled || false,
      quantity: autoTopUpData?.quantity || "",
    },
  });

  const enabledValue = watch("enabled");
  const quantityValue = watch("quantity");

  // Update form values when modal opens or autoTopUpData changes
  useEffect(() => {
    if (show && autoTopUpData) {
      setValue("enabled", autoTopUpData.enabled || false);
      setValue("quantity", autoTopUpData.quantity || "");
    }
  }, [show, autoTopUpData, setValue]);

  const handleSave = () => {
    const formValues = getValues();
    onSave({
      enabled: formValues.enabled,
      quantity: +formValues.quantity || 0,
    });
    //onHide();
  };

  const handleClose = () => {
    setValue("enabled", false);
    setValue("quantity", "");
    clearErrors();
    onHide();
  };

  const handleToggleChange = (enabled) => {
    if (hasPaymentMethod) {
      setValue("enabled", enabled);
    }
  };

  const handleQuantityChange = (value) => {
    setValue("quantity", value);
  };

  return (
    <Modal show={show} onHide={handleClose} centered size="md">
      <Modal.Header>
        <h4>{t("auto_top_up_settings")}</h4>
        <div className="d-flex justify-content-end">
          <ActionIconContainer onClick={handleClose}>
            <i className="fi fi-rr-cross-small" />
          </ActionIconContainer>
        </div>
      </Modal.Header>

      <Modal.Body>
        {!hasPaymentMethod && (
          <Alert variant="danger" className="mb-3">
            <div className="d-flex align-items-center">
              <i
                className="fi fi-rr-exclamation me-2"
                style={{ fontSize: "16px", position: "relative", top: "2px" }}
              ></i>
              <span>
                <Trans
                  i18nKey="add_payment_method_for_auto_topup_msg"
                  t={t}
                  components={[
                    <LinkAnchor
                      style={{ fontSize: "14px" }}
                      key="add-payment-method-link"
                      onClick={onAddPaymentMethod}
                    />,
                  ]}
                />
              </span>
            </div>
          </Alert>
        )}

        <div className="mb-3">
          <div className="d-flex justify-content-between align-items-center">
            <FormGroupLabel className="m-0">{t("auto_top_up")}</FormGroupLabel>
            <Switch
              id={"auto-top-up-switch"}
              isOn={enabledValue}
              onColor={colorSwitchActive}
              handleToggle={(e) => handleToggleChange(e.target.checked)}
              isDisabled={!hasPaymentMethod}
            />
          </div>
        </div>

        {enabledValue && (
          <>
            <div className="mb-3">
              <div className="d-flex justify-content-between align-items-center">
                <FormGroupLabel className="m-0">{t("quantity")}</FormGroupLabel>
                <div className="d-flex flex-column gap-1">
                  <FormItem style={{ minWidth: "220px" }}>
                    <FormField
                      name="quantity"
                      control={control}
                      component="input"
                      type="number"
                      onKeyDown={handleNegative}
                      onWheel={handleNumberInputOnWheelPreventChange}
                      step="5"
                      min="5"
                      errors={errors}
                      defaultValue=""
                      placeholder={t("enter_credits_placeholder")}
                      style={{
                        ...formInputStyles,

                        opacity: !hasPaymentMethod ? 0.5 : 1,
                        cursor: !hasPaymentMethod ? "not-allowed" : "text",
                      }}
                      disabled={!hasPaymentMethod}
                      onChange={(e) => {
                        const value = e.target.value;
                        handleQuantityChange(value);

                        // Clear errors first
                        clearErrors("quantity");

                        // Trigger validation for multiple of 5
                        const numValue = parseInt(value);
                        if (numValue > 0 && numValue % 5 !== 0) {
                          setError("quantity", {
                            type: "manual",
                            message: t("amount_must_be_multiple_of_5"),
                          });
                        }
                      }}
                    />
                  </FormItem>
                  {errors?.quantity && (
                    <Error error={errors.quantity.message} />
                  )}
                </div>
              </div>
            </div>

            {quantityValue > 0 && (
              <div
                className="p-2 mt-3"
                style={{
                  border: `1px solid #f5f5f5`,
                  borderRadius: "5px",
                  background: "#f5f5f5",
                  opacity: !hasPaymentMethod ? 0.5 : 1,
                }}
              >
                <span className="text-muted">
                  {/* {t("when_balance_reaches_zero_msg", { count: +quantity })} */}
                  <Trans
                    i18nKey="when_balance_reaches_zero_msg"
                    t={t}
                    values={{
                      count: +quantityValue,
                    }}
                  />
                </span>
              </div>
            )}
          </>
        )}
      </Modal.Body>

      <Modal.Footer className="d-flex justify-content-between">
        <FilterButton type="button" onClick={handleClose}>
          {t("cancel")}
        </FilterButton>
        <AddFormButton
          type="button"
          onClick={handleSave}
          disabled={
            !hasPaymentMethod ||
            !quantityValue ||
            +quantityValue <= 0 ||
            +quantityValue % 5 !== 0 ||
            saveAutoTopUpLoading
          }
        >
          {saveAutoTopUpLoading ? <CustomLoader /> : t("save_auto_top_up")}
        </AddFormButton>
      </Modal.Footer>
    </Modal>
  );
};

export default AutoTopUpSettingsModal;
